// @ts-strict-ignore
import { Checkbox, CheckboxProps } from './Checkbox'
import { FieldControl } from './FieldControl'
import { Input, InputProps } from './Input'
import { Select, SelectProps } from './Select'
import { Textarea, TextareaProps } from './Textarea'
import { useField } from 'formik'
import { useTranslation } from 'next-i18next'
import React, { FC } from 'react'
import { GridColumnProps, MarginBottomProps, MarginTopProps } from 'styled-system'

type InputType = InputProps & {
  field: 'input'
}

type TextareaType = TextareaProps & {
  field: 'textarea'
}
type SelectType = SelectProps & {
  field: 'select'
}
type CheckboxType = CheckboxProps & {
  field: 'checkbox'
}

type FieldType = InputType | TextareaType | SelectType | CheckboxType

type Props = MarginBottomProps &
  MarginTopProps &
  GridColumnProps & {
    label?: React.ReactNode | string
    assistiveText?: string
    errorMsg?: string
  }

export const Field: FC<FieldType & Props> = ({
  field = 'input',
  label,
  assistiveText,
  gridColumn,
  mt,
  marginTop,
  mb,
  marginBottom,
  ...rest
}) => {
  const { t } = useTranslation('rolex')
  // The ts-ignores and any are a bit messy, but the most important thing
  // is that the Field component is correctly typed based on the value of the field prop and that works.
  // If you use Field and set field as "input", e.g. <Field field="input" />,
  // field will be typed with the Input type 🤘
  const [fieldProps, meta] = useField<any>(rest.name)
  const error = meta.error?.replace('forms.', '')
  switch (field) {
    case 'input':
      return (
        <FieldControl
          gridColumn={gridColumn}
          label={label}
          assistiveText={assistiveText}
          errorMsg={meta.touched && meta.error ? t(error, { ns: 'rolex' }) : undefined}
        >
          {/*
          // @ts-ignore */}
          <Input {...fieldProps} {...rest} />
        </FieldControl>
      )
    case 'textarea':
      // Check out the contact form on the Rolex contact page.
      // Textareas only seem to have placeholders, no labels
      return (
        <FieldControl
          assistiveText={assistiveText}
          errorMsg={meta.touched && meta.error ? t(error, { ns: 'rolex' }) : undefined}
        >
          {/*
          // @ts-ignore */}
          <Textarea {...fieldProps} {...rest} />
        </FieldControl>
      )
    case 'select':
      return (
        <FieldControl
          assistiveText={assistiveText}
          errorMsg={meta.touched && meta.error ? t(error, { ns: 'rolex' }) : undefined}
        >
          {/*
          // @ts-ignore */}
          <Select {...fieldProps} {...rest} />
        </FieldControl>
      )
    case 'checkbox':
      return (
        <FieldControl
          mt="-10px"
          mb="0"
          isCheckbox
          assistiveText={assistiveText}
          errorMsg={meta.touched && meta.error ? t(error, { ns: 'rolex' }) : undefined}
        >
          {/*
          // @ts-ignore */}
          <Checkbox checked={fieldProps.value} label={label} {...fieldProps} {...rest} />
        </FieldControl>
      )
    default:
      return null
  }
}
